import { Helmet } from "react-helmet-async";
import { ImageIcon, Film, Paintbrush } from "lucide-react";
import TestimonialCard from "@/components/TestimonialCard";
import ServiceContactForm from "@/components/ServiceContactForm";

const services = [
  {
    category: "Photo",
    title: "Editing",
    icon: Paintbrush,
    description:
      "Our team is equipped to handle your photo editing for you. We've edited for photographers that have their work published in magazines and more.",
  },
  {
    category: "Video & Film",
    title: "Editing",
    icon: Film,
    description:
      "Already shot the content but need your film edited? We provide video editing services that can help lighten your load. Who knows — maybe you can even start filming your next project while you wait!",
  },
  {
    category: "Photo",
    title: "Restoration",
    icon: ImageIcon,
    description:
      "We know how important photos are. Especially those filled with old memories. Maybe they're scratched or little Jon Jon wasted coffee all over that picture of grandma Sue. We can help bring those memories back to life through our photo restoration service.",
  },
];

const team = [
  {
    name: "Cedric Searcy",
    image: "/__l5e/assets-v1/73d82765-14f5-40bb-a355-19157676f2fd/Personnel-Cedric-700x853.jpg",
  },
  {
    name: "Tabitha Suggs-Searcy",
    image: "/__l5e/assets-v1/85981944-86fd-4313-9c08-da9c6b1d30c4/tab-editorial-1-700x935.jpg",
  },
  {
    name: "The Pentaxx Guy",
    image: "/__l5e/assets-v1/7f7074a1-3580-4788-b7b5-c7d6426cdda9/pentaxx-guy-102-700x935.jpg",
  },
  {
    name: "Gary FRLN Jackson",
    image: "/__l5e/assets-v1/9bb34835-244e-4dc0-ba3e-e56be3733636/gary-700x853.jpg",
  },
  {
    name: "Rachael Banks",
    image: "/__l5e/assets-v1/cc320053-17da-47ba-a9b1-f41fc76e913b/rach-banks-web-1-700x853.jpg",
  },
  {
    name: "Deontae Holloway",
    image: "/__l5e/assets-v1/ddec478d-7ab3-410d-a0d6-b4719a6a29c8/deontae-700x853.jpg",
  },
];

const PostProductionPage = () => (
  <div className="pt-20">
    <Helmet>
      <title>Post Production Columbus GA | Photo & Video Editing</title>
      <meta name="description" content="Professional photo editing, video editing, and photo restoration services in Columbus, GA. Color grading and retouching." />
      <link rel="canonical" href="https://bookmystudiospace.com/post-production" />
      <meta property="og:title" content="Post Production Columbus GA | Photo & Video Editing" />
      <meta property="og:description" content="Professional photo editing, video editing, and photo restoration services in Columbus, GA. Color grading and retouching." />
      <meta property="og:url" content="https://bookmystudiospace.com/post-production" />
    </Helmet>
    {/* Hero */}
    <section className="py-20 lg:py-28 text-center">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-4">
          POST PRODUCTION
        </h1>
        <p className="text-muted-foreground text-lg font-body max-w-2xl mx-auto">
          PHOTO EDITING – VIDEO EDITING – PHOTO RESTORATION
        </p>
      </div>
    </section>

    {/* Description */}
    <section className="pb-16">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="bg-panel border border-border p-8 text-center">
          <Paintbrush className="w-12 h-12 text-primary mx-auto mb-6" />
          <p className="text-foreground/80 font-body leading-relaxed">
            You're not alone! Our team is ready to assist you with the final
            phase of your project. Whether you need help putting the finishing
            touches on your photos or the finishing touches on your video, we
            can help finish the job. We provide photo editing services as well
            as video and photo restoration services.
          </p>
        </div>
      </div>
    </section>

    {/* Services Grid */}
    <section className="pb-20">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((s) => (
            <div
              key={`${s.category}-${s.title}`}
              className="bg-panel border border-border p-8 text-center"
            >
              <s.icon className="w-10 h-10 text-primary mx-auto mb-3" />
              <p className="text-xs text-muted-foreground font-body uppercase tracking-widest mb-1">
                {s.category}
              </p>
              <h2 className="text-xl font-display tracking-wider mb-4">
                {s.title}
              </h2>
              <p className="text-sm text-foreground/80 font-body leading-relaxed">
                {s.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Meet the Team */}
    <section className="pb-20">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl md:text-4xl font-display tracking-widest text-center mb-4">
          MEET SOME OF THE STUDIO REGULARS
        </h2>
        <p className="text-muted-foreground font-body text-center mb-12">
          We all shoot here in some capacity
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
          {team.map((p) => (
            <div key={p.name} className="group text-center">
              <div className="aspect-[3/4] overflow-hidden border border-border mb-3">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <p className="text-sm font-display tracking-wider">{p.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Testimonial */}
    <TestimonialCard
      quote="The team at MyStudio handled all of our post-production needs flawlessly. From color grading to final edits, they delivered exactly what we envisioned. Highly recommend their services!"
      name="Marcus Thompson"
      title="Content Creator"
      image="/__l5e/assets-v1/73d82765-14f5-40bb-a355-19157676f2fd/Personnel-Cedric-700x853.jpg"
    />

    {/* Contact Form */}
    <ServiceContactForm defaultService="Post Production" />
  </div>
);

export default PostProductionPage;
